import { Button } from '@/components/primitives/Button';
/**
 * AssessmentDraftForm — input form for the Assessment Drafter
 * (Phase X.3.5 → X.3.5.4 SPICE mode → 2026-07 gap-interview redesign).
 *
 * The client identity (recipient, first/last name, pronouns) moved OUT of
 * this form into the page-level AssessmentClientBlock (founder ruling:
 * ONE client block; the whole draft reads from it). This form keeps the
 * notes field (the actual content), the optional details, the mode
 * toggle, and Generate; the client values arrive as props. There is NO
 * credentials field (signature-integrity P0): author identity, name and
 * credentials both, comes from the authenticated profile (staff_roster),
 * never from form input.
 *
 * Pronouns ride generate calls in BOTH modes: the server resolves them for
 * the functional lead-in, the closing courtesy sentence and the prompt's
 * pronoun line (Care Management) and the full report register (SPICE).
 * SPICE requires an explicit choice. Care Management asks for a choice once
 * a client is picked; an unset choice otherwise resolves to they/them.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { ASSESSMENT_NOTES_DRAFT_PREFIX, readStoredDraft, writeStoredDraft } from '@/lib/draftAutosave';
import { useTenant } from '@/lib/auth';
import { aiGenerateKeyOf } from '@/lib/errorCopy';
import { ErrorNotice } from '@/components/ErrorNotice';
import {
  aiToolsApi,
  type ApiError,
  type AssessmentDraftInput,
  type AssessmentDraftResponse,
  type AssessmentFormInputSnapshot,
  type AssessmentMode,
  type SpiceAssessmentDraftResponse,
  type SpicePronounChoice,
} from '@/lib/api';
import { EmailModeToggle, type EmailMode } from './EmailModeToggle';
import { DateInput } from '@/components/primitives/DateInput';

export interface AssessmentDraftFormProps {
  hasDraft?: boolean;
  tenantSlug: string;
  /** Controlled mode (lifted to the page in X.6.4B). */
  mode: EmailMode;
  onModeChange: (mode: EmailMode) => void;
  /** True when the page fixed the mode for this viewer (a SPICE-only
   *  viewer has no switch); the Mode toggle does not render. */
  modeFixed?: boolean;
  /** From the page-level client block (the ONE client source of truth). */
  recipient: string;
  clientFirstName: string;
  clientLastName: string;
  /** Resolved pronoun choice from the client block; undefined = unset. */
  pronouns?: SpicePronounChoice;
  /** False while a custom pronoun set is incomplete; Generate waits. */
  pronounsComplete: boolean;
  /** True while a bound client needs a choice before Generate. */
  pronounsRequired: boolean;
  /** True while an added recommendation waits to be re-worded; Generate waits. */
  picksPending: boolean;
  /** Fired on generation start/finish so the page can show a drafting state. */
  onGeneratingChange?: (generating: boolean) => void;
  onDraftReady: (
    response: AssessmentDraftResponse | SpiceAssessmentDraftResponse,
    formInput: AssessmentFormInputSnapshot,
    mode: AssessmentMode,
  ) => void;
  /**
   * A saved report reopened in the builder: the notes and visit details the
   * report was written from. The notes autosave under the draft's own key,
   * so reopening one never overwrites notes typed for a new report.
   */
  resumed?: { draftId: string; formInput: AssessmentFormInputSnapshot };
  /** The fields as kept (src/lib/keptWork.ts), put back over `resumed`. */
  keptFields?: AssessmentFormFields;
  /** Every change to the fields, so the page keeps the whole form. */
  onFieldsChange?: (fields: AssessmentFormFields) => void;
}

/** The form's own fields; the page holds the client block. */
export interface AssessmentFormFields {
  notes: string;
  age: string;
  assessmentDate: string;
  location: string;
  attendees: string;
}

const NOTES_MIN = 200;

/**
 * Input parity with the Kith composer (2026-07 redesign): the notes
 * ceiling is VISIBLE (maxLength + microcopy), never a silent read
 * ceiling. KEEP IN SYNC with NOTES_MAX_CHARS in
 * api/src/functions/aiToolsAssessmentDraft.ts. Long transcripts are
 * normal input, so this sits well above the composer's 20k backstop.
 */
export const ASSESSMENT_NOTES_MAX = 60000;

/** Show the quiet character count only past this (no scolding copy). */
const COUNTER_VISIBLE_FROM = 1500;

function notesDraftKey(tenantSlug: string, draftId?: string): string {
  return draftId === undefined
    ? `${ASSESSMENT_NOTES_DRAFT_PREFIX}:${tenantSlug}`
    : `${ASSESSMENT_NOTES_DRAFT_PREFIX}:${tenantSlug}:${draftId}`;
}

const INPUT_CLS =
  'mt-1.5 block w-full min-h-11 rounded-[12px] border border-sage-400/20 bg-paper px-3.5 py-2.5 text-sm text-ink placeholder:text-sage-300 outline-none transition-[border-color,box-shadow] duration-[var(--dur-base)] ease-quiet focus:border-sage-400 focus:ring-[3px] focus:ring-sage-800/[6%]';
const LABEL_CLS = 'block text-[0.8125rem] font-semibold text-sage-800';

function trimOrUndefined(v: string): string | undefined {
  return v.trim().length > 0 ? v.trim() : undefined;
}

export function AssessmentDraftForm({
  hasDraft = false,
  tenantSlug,
  mode,
  onModeChange,
  modeFixed = false,
  recipient,
  clientFirstName,
  clientLastName,
  pronouns,
  pronounsComplete,
  pronounsRequired,
  picksPending,
  onGeneratingChange,
  onDraftReady,
  resumed,
  keptFields,
  onFieldsChange,
}: AssessmentDraftFormProps): ReactNode {
  // The page keeps the whole form, these fields included (onFieldsChange,
  // src/lib/keptWork.ts). Notes typed before that, under the notes draft
  // key, are read once and moved over, stamped with and restored only to
  // the person signed in.
  const draftAuthor = useTenant().principal?.userId ?? null;
  const notesKey = notesDraftKey(tenantSlug, resumed?.draftId);
  const [notes, setNotes] = useState(
    () => keptFields?.notes ?? (readStoredDraft(notesKey, draftAuthor) || (resumed?.formInput.notes ?? '')),
  );
  useEffect(() => {
    if (draftAuthor !== null) writeStoredDraft(notesKey, '', null);
  }, [notesKey, draftAuthor]);
  const [age, setAge] = useState(keptFields?.age ?? resumed?.formInput.client_age ?? '');
  const [assessmentDate, setAssessmentDate] = useState(keptFields?.assessmentDate ?? resumed?.formInput.assessment_date ?? '');
  const [location, setLocation] = useState(keptFields?.location ?? resumed?.formInput.assessment_location ?? '');
  const [attendees, setAttendees] = useState(keptFields?.attendees ?? resumed?.formInput.attendees ?? '');
  useEffect(() => {
    onFieldsChange?.({ notes, age, assessmentDate, location, attendees });
  }, [notes, age, assessmentDate, location, attendees]);
  // WS-9: the registry key for the last failure (errorCopy.ts owns the
  // copy); null = no error banner; an off-registry key renders the
  // honest needs-platform generic.
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const isSpice = mode === 'spice';

  const snapshot = (): AssessmentFormInputSnapshot => ({
    recipient: recipient.trim(),
    client_first_name: clientFirstName.trim(),
    client_last_name: clientLastName.trim(),
    notes: notes.trim(),
    client_age: trimOrUndefined(age),
    assessment_date: trimOrUndefined(assessmentDate),
    assessment_location: trimOrUndefined(location),
    attendees: trimOrUndefined(attendees),
    // Every generation records a choice: SPICE always has one, and Care
    // Management's unset choice is the they/them the server resolves.
    pronouns: isSpice ? pronouns : (pronouns ?? { preset: 'they_them' }),
  });

  const buildInput = (): AssessmentDraftInput => {
    const s = snapshot();
    return {
      recipient: s.recipient,
      client_first_name: s.client_first_name,
      client_last_name: s.client_last_name,
      notes: s.notes,
      client_age: s.client_age,
      assessment_date: s.assessment_date,
      assessment_location: s.assessment_location,
      attendees: s.attendees,
      mode,
      // Both modes: the server resolves the forms (functional lead-in +
      // courtesy sentence in CM; the whole register in SPICE). Unset falls
      // back to they/them server-side.
      ...(pronouns ? { pronouns } : {}),
    };
  };

  // The request, the snapshot the result is filed with and the mode are
  // read at the press and travel as the variables, so the draft is built
  // from what was on screen when Generate was pressed.
  const mutation = useMutation({
    mutationFn: (v: { input: AssessmentDraftInput; snapshot: AssessmentFormInputSnapshot; mode: typeof mode }) =>
      aiToolsApi.draftAssessment(tenantSlug, v.input),
    onSuccess: (res, v) => {
      setErrorKey(null);
      onDraftReady(res, v.snapshot, v.mode);
    },
    // WS-6 (2026-07-10) carried into the WS-9 registry: max_tokens is the
    // real cause behind most "shape I could not use" reports, and the
    // split-the-notes instruction is the action that actually works.
    onError: (e: ApiError) => {
      setErrorKey(aiGenerateKeyOf(e) ?? 'unknown');
    },
  });

  // X.3.5.1: elapsed-time indicator. Assessment runs on Opus (~couple of
  // minutes); the streaming response keeps the connection alive past the SWA
  // edge cap, and this counter reassures the operator during the wait.
  const [elapsed, setElapsed] = useState(0);
  const generating = mutation.isPending;
  useEffect(() => {
    onGeneratingChange?.(generating);
    if (!generating) {
      setElapsed(0);
      return;
    }
    setElapsed(0);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [generating, onGeneratingChange]);

  const notesShort = notes.trim().length > 0 && notes.trim().length < NOTES_MIN;
  // A custom set needs all five forms. SPICE needs a choice; Care Management
  // needs one only for a bound client, and otherwise resolves they/them.
  const pronounsReady =
    pronounsComplete && (pronouns !== undefined || (!isSpice && !pronounsRequired));
  const canSubmit =
    recipient.trim().length > 0 &&
    clientFirstName.trim().length > 0 &&
    clientLastName.trim().length > 0 &&
    notes.trim().length >= NOTES_MIN &&
    pronounsReady &&
    !picksPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({ input: buildInput(), snapshot: snapshot(), mode });
      }}
      className="space-y-4"
      data-testid="assessment-draft-form"
    >
      {/* X.3.5.4: Care Management ↔ SPICE. SPICE swaps the report + drops
          billing. Hidden when the page fixed the mode for this viewer. */}
      {!modeFixed && (
        <EmailModeToggle
          mode={mode}
          onChange={onModeChange}
          disabled={generating}
          contextNoun="Report"
          testId="assessment-mode-toggle"
        />
      )}

      {isSpice && pronouns === undefined ? (
        <p className="text-xs text-ink-soft" data-testid="spice-pronoun-hint">
          Choose the client's pronouns in the client block above; a SPICE report needs them.
        </p>
      ) : !pronounsComplete ? (
        <p className="text-xs text-ink-soft" data-testid="assessment-pronoun-hint">
          Fill in all five pronoun forms in the client block above, or choose a set.
        </p>
      ) : pronouns === undefined && pronounsRequired ? (
        <p className="text-xs text-ink-soft" data-testid="assessment-pronoun-hint">
          Choose the client's pronouns in the client block above. The report and the recommendations
          use them.
        </p>
      ) : pronouns === undefined ? (
        <p className="text-xs text-ink-soft" data-testid="assessment-pronoun-default-note">
          {"The report uses 'they' and 'them' until you choose pronouns."}
        </p>
      ) : null}

      <div>
        <label htmlFor="assess-notes" className={LABEL_CLS}>
          Notes, observations, or transcript
        </label>
        <textarea
          id="assess-notes"
          value={notes}
          maxLength={ASSESSMENT_NOTES_MAX}
          onChange={(e) => {
            const next = e.target.value;
            setNotes(next.length > ASSESSMENT_NOTES_MAX ? next.slice(0, ASSESSMENT_NOTES_MAX) : next);
          }}
          rows={10}
          disabled={mutation.isPending}
          aria-describedby="assessment-notes-counter"
          placeholder="Paste anything you've got: assessment observations, email correspondence with the family, prior notes, your own follow-up thoughts. The more context, the sharper the draft."
          className={INPUT_CLS}
          data-testid="assessment-notes"
        />
        <p
          id="assessment-notes-counter"
          className="mt-1.5 text-xs text-ink-soft"
          data-testid="assessment-notes-counter"
        >
          {notes.length > COUNTER_VISIBLE_FROM && (
            <span data-testid="assessment-notes-count">
              {notes.length.toLocaleString()} of {ASSESSMENT_NOTES_MAX.toLocaleString()} characters ·{' '}
            </span>
          )}
          Paste freely, up to {ASSESSMENT_NOTES_MAX.toLocaleString()} characters. Signatures and
          boilerplate are stripped automatically.
        </p>
        {notesShort && (
          <p className="text-xs text-ink-soft mt-0.5" data-testid="assessment-notes-hint">
            A bit more to work with helps. Aim for at least {NOTES_MIN} characters of notes
            ({notes.trim().length} so far).
          </p>
        )}
      </div>

      <details className="rounded-[12px] border border-sage-400/20 p-3">
        <summary className="text-[0.8125rem] font-semibold text-sage-800 cursor-pointer">
          Optional details
        </summary>
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="assess-age" className={LABEL_CLS}>
                Client age
              </label>
              <input
                id="assess-age"
                type="number"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                disabled={mutation.isPending}
                className={INPUT_CLS}
              />
            </div>
            <div>
              <label htmlFor="assess-date" className={LABEL_CLS}>
                {isSpice ? 'Visit date' : 'Assessment date'}
              </label>
              <DateInput
                id="assess-date"
                value={assessmentDate}
                onChange={(e) => setAssessmentDate(e.target.value)}
                disabled={mutation.isPending}
                size="field" className="mt-1.5 block w-full"
              />
            </div>
          </div>
          <div>
            <label htmlFor="assess-location" className={LABEL_CLS}>
              {isSpice ? 'Visit location' : 'Assessment location'}
            </label>
            <input
              id="assess-location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              disabled={mutation.isPending}
              placeholder="Client's home, ABC Assisted Living, etc."
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label htmlFor="assess-attendees" className={LABEL_CLS}>
              {isSpice ? 'Visiting team' : 'Who was present'}
            </label>
            <input
              id="assess-attendees"
              type="text"
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              disabled={mutation.isPending}
              placeholder={
                isSpice
                  ? 'SPICE therapist(s) who visited'
                  : 'Client, daughter Sarah, facility director Marina, etc.'
              }
              className={INPUT_CLS}
            />
          </div>
        </div>
      </details>

      {errorKey !== null && <ErrorNotice surface="assessment_generate" code={errorKey} />}

      <Button
        type="submit"
        disabled={!canSubmit || generating}
        variant={hasDraft ? 'ghost' : 'primary'}
        size={hasDraft ? 'sm' : 'md'}
      >
        <Sparkles size={17} aria-hidden="true" />
        {generating
          ? `Generating your ${isSpice ? 'SPICE report' : 'assessment report'}. This usually takes a couple of minutes, feel free to set this aside and come back…${elapsed > 15 ? ` (${elapsed}s)` : ''}`
          : isSpice
            ? 'Generate SPICE report'
            : 'Generate assessment report'}
      </Button>
    </form>
  );
}
